/**
 * ICE tables: Initial, Change, Equilibrium.
 *
 * For aA + bB ⇌ cC + dD with the reaction advancing by an extent x (mol/L), each species
 * moves by its coefficient times x, down for a reactant and up for a product:
 *
 *     [i] = [i]₀ + ν_i·x         (ν_i = −coefficient for a reactant, +coefficient for a product)
 *
 * and the equilibrium constant is K = [C]^c[D]^d ÷ [A]^a[B]^b, leaving out pure solids and
 * liquids. Q(x), the same expression at any x, rises steadily from 0 (a product used up) to
 * ∞ (a reactant used up), so Q(x) = K has exactly one root among the physically possible
 * x. `solveExtent` finds it by bisection, then refines it from the nearer end of the allowed
 * range in log space so that a reactant left at 10⁻¹² M is still found to full precision.
 * There is no "x is small" approximation in the answer; `largestShift` reports how small x
 * actually was, so you can see whether the 5 % shortcut would have worked.
 */

export interface EquilibriumSpecies {
  label: string;
  /** Positive. */
  coefficient: number;
  side: "reactant" | "product";
  /** A pure solid or liquid: shown in the table but left out of K. */
  solid: boolean;
}

const BISECTIONS = 400;
const REFINEMENTS = 300;
const MIN_LOG = -320;
const EXPAND_LIMIT = 2000;

function direction(species: EquilibriumSpecies): number {
  return species.side === "reactant" ? -1 : 1;
}

/** Q for a set of concentrations; solids are skipped. 0 or Infinity at the ends. */
export function quotient(
  species: readonly EquilibriumSpecies[],
  concentrations: readonly number[]
): number {
  let numerator = 1;
  let denominator = 1;
  for (const [index, item] of species.entries()) {
    if (item.solid) {
      continue;
    }
    const term = concentrations[index] ** item.coefficient;
    if (item.side === "product") {
      numerator *= term;
    } else {
      denominator *= term;
    }
  }
  if (denominator === 0) {
    return numerator === 0 ? Number.NaN : Number.POSITIVE_INFINITY;
  }
  return numerator / denominator;
}

export function concentrationsAt(
  species: readonly EquilibriumSpecies[],
  initial: readonly number[],
  x: number
): number[] {
  return species.map((item, index) =>
    item.solid
      ? 0
      : Math.max(initial[index] + direction(item) * item.coefficient * x, 0)
  );
}

export interface Bounds {
  low: number;
  high: number;
  /** Index of the species that sets each finite bound, or -1. */
  lowSpecies: number;
  highSpecies: number;
}

/** The range of x over which no concentration goes negative. */
export function extentBounds(
  species: readonly EquilibriumSpecies[],
  initial: readonly number[]
): Bounds {
  let low = Number.NEGATIVE_INFINITY;
  let high = Number.POSITIVE_INFINITY;
  let lowSpecies = -1;
  let highSpecies = -1;
  for (const [index, item] of species.entries()) {
    if (item.solid) {
      continue;
    }
    const limit = initial[index] / item.coefficient;
    if (item.side === "product" && -limit > low) {
      low = -limit;
      lowSpecies = index;
    }
    if (item.side === "reactant" && limit < high) {
      high = limit;
      highSpecies = index;
    }
  }
  return { low, high, lowSpecies, highSpecies };
}

export type ExtentResult =
  | { ok: true; x: number; concentrations: number[] }
  | { ok: false; reason: "badK" | "noStart" | "noSolution" };

function expandBounds(
  species: readonly EquilibriumSpecies[],
  initial: readonly number[],
  k: number,
  bounds: Bounds
): { low: number; high: number } | null {
  let { low, high } = bounds;
  if (high === Number.POSITIVE_INFINITY) {
    high = Math.max(1, Number.isFinite(low) ? Math.abs(low) * 2 : 1);
    for (let i = 0; i < EXPAND_LIMIT; i++) {
      if (quotient(species, concentrationsAt(species, initial, high)) > k) {
        break;
      }
      high *= 2;
    }
  }
  if (low === Number.NEGATIVE_INFINITY) {
    low = -Math.max(1, Number.isFinite(high) ? Math.abs(high) * 2 : 1);
    for (let i = 0; i < EXPAND_LIMIT; i++) {
      if (quotient(species, concentrationsAt(species, initial, low)) < k) {
        break;
      }
      low *= 2;
    }
  }
  return Number.isFinite(low) && Number.isFinite(high) ? { low, high } : null;
}

/**
 * Concentrations measured from one end of the allowed range: `y` is the distance from that
 * end, so the species that runs out there is exactly `coefficient × y` and not a difference
 * of two nearly equal numbers.
 */
function anchoredConcentrations(
  species: readonly EquilibriumSpecies[],
  initial: readonly number[],
  anchor: { x: number; species: number; upper: boolean },
  y: number
): number[] {
  const x = anchor.upper ? anchor.x - y : anchor.x + y;
  return species.map((item, index) => {
    if (item.solid) {
      return 0;
    }
    const step = direction(item) * item.coefficient;
    const base =
      index === anchor.species ? 0 : initial[index] + step * anchor.x;
    const moved = base + (anchor.upper ? -step * y : step * y);
    return index === anchor.species
      ? Math.abs(step) * y
      : Math.max(moved, 0) + 0 * x;
  });
}

function refine(
  species: readonly EquilibriumSpecies[],
  initial: readonly number[],
  k: number,
  anchor: { x: number; species: number; upper: boolean },
  span: number
): { x: number; concentrations: number[] } {
  let lowLog = MIN_LOG;
  let highLog = Math.log10(span);
  for (let i = 0; i < REFINEMENTS; i++) {
    const mid = (lowLog + highLog) / 2;
    const q = quotient(
      species,
      anchoredConcentrations(species, initial, anchor, 10 ** mid)
    );
    // Moving away from the lower end raises Q; moving away from the upper end lowers it.
    const tooHigh = anchor.upper ? q < k : q > k;
    if (tooHigh) {
      highLog = mid;
    } else {
      lowLog = mid;
    }
  }
  const y = 10 ** ((lowLog + highLog) / 2);
  return {
    x: anchor.upper ? anchor.x - y : anchor.x + y,
    concentrations: anchoredConcentrations(species, initial, anchor, y),
  };
}

/** Solve Q(x) = K for the extent of reaction. */
export function solveExtent(
  species: readonly EquilibriumSpecies[],
  initial: readonly number[],
  k: number
): ExtentResult {
  if (!(k > 0 && Number.isFinite(k))) {
    return { ok: false, reason: "badK" };
  }
  const bounds = extentBounds(species, initial);
  if (bounds.low === 0 && bounds.high === 0) {
    return { ok: false, reason: "noStart" };
  }
  const range = expandBounds(species, initial, k, bounds);
  if (range === null) {
    return { ok: false, reason: "noSolution" };
  }
  let { low, high } = range;
  const span = high - low;
  for (let i = 0; i < BISECTIONS; i++) {
    const mid = (low + high) / 2;
    if (quotient(species, concentrationsAt(species, initial, mid)) > k) {
      high = mid;
    } else {
      low = mid;
    }
  }
  const rough = (low + high) / 2;
  const nearLow = rough - range.low <= range.high - rough;
  const finiteAnchor = nearLow ? bounds.low : bounds.high;
  if (!Number.isFinite(finiteAnchor)) {
    return {
      ok: true,
      x: rough,
      concentrations: concentrationsAt(species, initial, rough),
    };
  }
  const refined = refine(
    species,
    initial,
    k,
    {
      x: finiteAnchor,
      species: nearLow ? bounds.lowSpecies : bounds.highSpecies,
      upper: !nearLow,
    },
    span
  );
  return { ok: true, ...refined };
}

/** The largest fraction of any starting concentration that the reaction changed. */
export function largestShift(
  species: readonly EquilibriumSpecies[],
  initial: readonly number[],
  x: number
): number | null {
  let largest: number | null = null;
  for (const [index, item] of species.entries()) {
    const change = Math.abs(item.coefficient * x);
    const consumed =
      (item.side === "reactant" && x > 0) || (item.side === "product" && x < 0);
    if (item.solid || !consumed || !(initial[index] > 0)) {
      continue;
    }
    const share = change / initial[index];
    if (largest === null || share > largest) {
      largest = share;
    }
  }
  return largest;
}

export type Direction = "forward" | "reverse" | "equilibrium";

const AT_EQUILIBRIUM = 1e-9;

/** Which way the mixture moves: Q below K runs forward, above K runs in reverse. */
export function reactionDirection(q: number, k: number): Direction {
  if (Math.abs(q - k) <= AT_EQUILIBRIUM * k) {
    return "equilibrium";
  }
  return q < k ? "forward" : "reverse";
}

/** The extent implied by one known equilibrium concentration. */
export function extentFromKnown(
  species: readonly EquilibriumSpecies[],
  initial: readonly number[],
  index: number,
  equilibrium: number
): number {
  const item = species[index];
  return (equilibrium - initial[index]) / (direction(item) * item.coefficient);
}

/** True when every concentration in the (unclamped) table is at least zero. */
export function isPhysical(
  species: readonly EquilibriumSpecies[],
  initial: readonly number[],
  x: number
): boolean {
  return species.every(
    (item, index) =>
      item.solid ||
      initial[index] + direction(item) * item.coefficient * x >= -1e-12
  );
}

/** Signed stoichiometric step for the "Change" column: −2 for 2A consumed, +1 for C formed. */
export function changeCoefficient(item: EquilibriumSpecies): number {
  return direction(item) * item.coefficient;
}
